/**
 * Vendor / client registration form — the company's VENDOR FORM and CLIENT
 * FORM (1. General information … 5. Tax & financial information), filled in
 * from the master record.  A field not filled in prints as an empty box, so
 * the blank form (no record) can go out to be filled in by hand.  The
 * example's 6. Declaration & authorization (signature lines) is left off.
 *
 * Kept free of html2pdf so the print page can import it without pulling the
 * PDF library in; generateRegistrationPdf.ts lazy-loads that.
 */
import { COMPANY_ADDRESS, COMPANY_GSTIN, COMPANY_NAME } from './boardPo';

/** The form's section headings — the master forms are laid out under them too. */
export const REG_SECTIONS = {
  general: '1. General Information',
  address: '2. Address Details',
  primary: '3. Contact Person Details (Primary)',
  secondary: '4. Contact Person Details (Secondary)',
  tax: '5. Tax & Financial Information',
} as const;

/** Offered for Business Type; anything else may be typed. */
export const BUSINESS_TYPES = [
  'Manufacturer', 'Trader / Dealer', 'Distributor', 'Service Provider', 'Job Worker / Contractor',
] as const;

export interface RegContact {
  name: string;
  dept: string;
  designation: string;
  phone: string;
  email: string;
  altMobile: string;
}

/** Everything the printed form shows. */
export interface RegistrationDoc {
  kind: 'VENDOR' | 'CLIENT';
  /** VEN001 / Cli_12; empty on the blank form. */
  code: string;
  companyName: string;
  registrationId: string;
  legalName: string;
  mainContactName: string;
  mainContactDesignation: string;
  businessType: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  /** A vendor's; a client's address has none. */
  country?: string;
  /** A client's. */
  dispatchAddress?: string;
  /** Client only: the CRM who looks after the account. */
  crmName?: string;
  /** Primary (1), Primary (2), Secondary (1), Secondary (2). */
  contacts: RegContact[];
  gstNo: string;
  panNo: string;
  bankName: string;
  bankBranch: string;
  accountNo: string;
  ifsc: string;
  msme: string;
  creditTerms: string;
  /** Other numbers the master keeps (CIN, Drug Licence…), two to a row. */
  otherNumbers?: Array<[label: string, value: string]>;
}

const s = (x: unknown) => (x == null ? '' : String(x).trim());
const same = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

/** A vendor record (GET /api/vendors/:id) as the form; {} is the blank form. */
export function vendorRegistration(v: Record<string, unknown>): RegistrationDoc {
  const phone = s(v.phone);
  const tin = s(v.tin);
  return {
    kind: 'VENDOR',
    code: s(v.id),
    companyName: s(v.name),
    registrationId: s(v.registrationId),
    legalName: s(v.legalName),
    mainContactName: s(v.mainContactName),
    mainContactDesignation: s(v.mainContactDesignation),
    businessType: s(v.businessType),
    phone: phone === '0' ? '' : phone,
    email: s(v.email),
    address: s(v.address),
    city: s(v.city),
    zip: s(v.zip),
    state: s(v.state),
    country: s(v.country),
    contacts: [1, 2, 3, 4].map((n) => ({
      name: s(v[`contact${n}Name`]),
      dept: s(v[`contact${n}Dept`]),
      designation: s(v[`contact${n}Designation`]),
      phone: s(v[`contact${n}Phone`]),
      email: s(v[`contact${n}Email`]),
      altMobile: s(v[`contact${n}AltMobile`]),
    })),
    gstNo: s(v.gstNo),
    panNo: s(v.panNo),
    bankName: s(v.bankName),
    bankBranch: s(v.bankBranch),
    accountNo: s(v.bankAccountNo),
    ifsc: s(v.bankIfsc),
    msme: s(v.msmeNo),
    creditTerms: s(v.creditTerms),
    otherNumbers: [
      // The old TIN field often holds the GSTIN itself — printed once.
      ...(tin && !same(tin, s(v.gstNo)) ? [['TIN No. (old)', tin] as [string, string]] : []),
      ...(s(v.fax) ? [['Fax', s(v.fax)] as [string, string]] : []),
    ],
  };
}

/** A client record (GET /api/clients/:id) as the form; {} is the blank form. */
export function clientRegistration(c: Record<string, unknown>): RegistrationDoc {
  // Contact Person 1–3 are Primary (1), Primary (2), Secondary (1); the 4th Secondary (2).
  const person = (p: string): RegContact => ({
    name: s(c[`${p}Name`]),
    dept: s(c[`${p}Dept`]),
    designation: s(c[`${p}Designation`]),
    phone: s(c[`${p}Phone`]),
    email: s(c[`${p}Email`]),
    altMobile: s(c[`${p}AltMobile`]),
  });
  const days = s(c.creditDays);
  const tin = s(c.tin);
  return {
    kind: 'CLIENT',
    code: s(c.id),
    companyName: s(c.name),
    registrationId: s(c.registrationId),
    legalName: s(c.legalName),
    mainContactName: s(c.mainContactName),
    mainContactDesignation: s(c.mainContactDesignation),
    businessType: s(c.businessType),
    phone: s(c.phone),
    email: s(c.email),
    address: s(c.billingAddress),
    dispatchAddress: s(c.dispatchAddress),
    crmName: s(c.crmName),
    city: s(c.city),
    zip: s(c.zip),
    state: s(c.state),
    contacts: ['contactPerson', 'contactPerson2', 'contactPerson3', 'contactPerson4'].map(person),
    gstNo: s(c.gstNo),
    panNo: s(c.panNo),
    bankName: s(c.bankName),
    bankBranch: s(c.bankBranch),
    accountNo: s(c.bankAccountNo),
    ifsc: s(c.bankIfsc),
    msme: s(c.msmeNo),
    creditTerms: days ? `${days} days` : '',
    otherNumbers: [
      ['CIN No.', s(c.cin)],
      ['Drug Licence No.', s(c.drugLic)],
      ...(tin && !same(tin, s(c.gstNo)) ? [['TIN No.', tin] as [string, string]] : []),
      ...(s(c.cst) ? [['CST No.', s(c.cst)] as [string, string]] : []),
    ],
  };
}

/** "Vendor Form - VEN001 - Name.pdf", safe as a file name. */
export function registrationFileName(d: RegistrationDoc): string {
  const who = d.kind === 'VENDOR' ? 'Vendor' : 'Client';
  const name = [`${who} Form`, d.code || 'Blank', d.companyName].filter(Boolean).join(' - ');
  return `${name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').slice(0, 120)}.pdf`;
}

function esc(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const LINE = '1px solid #000';
const ROW = 'height:21px;';
const LABEL = `border:${LINE};padding:2px 6px;font-weight:bold;font-size:10.5px;line-height:1.2;vertical-align:middle;text-align:left;`;
const VALUE = `border:${LINE};padding:2px 6px;font-size:11px;line-height:1.2;vertical-align:middle;overflow-wrap:anywhere;`;
// Section bands keep their grey when printed (browsers drop backgrounds otherwise).
const BAND = `border:${LINE};padding:3px 6px;font-weight:bold;font-size:11.5px;letter-spacing:0.3px;background:#e9e9e9;`
  + '-webkit-print-color-adjust:exact;print-color-adjust:exact;';

const label = (text: string) => `<td style="${LABEL}">${esc(text)}</td>`;
const value = (text: string, span = 1, extra = '') =>
  `<td colspan="${span}" style="${VALUE}${extra}">${esc(text).replace(/\r?\n/g, '<br>')}</td>`;
const full = (l: string, v: string, tall = false) =>
  `<tr style="${ROW}">${label(l)}${value(v, 3, tall ? 'height:34px;vertical-align:top;' : '')}</tr>`;
const pair = (l1: string, v1: string, l2: string, v2: string) =>
  `<tr style="${ROW}">${label(l1)}${value(v1)}${label(l2)}${value(v2)}</tr>`;
const band = (title: string) => `<tr><td colspan="4" style="${BAND}">${esc(title.toUpperCase())}</td></tr>`;

const blankContact: RegContact = { name: '', dept: '', designation: '', phone: '', email: '', altMobile: '' };

function contactRows(a: RegContact = blankContact, b: RegContact = blankContact): string {
  return [
    pair('Contact Person Name (1)', a.name, 'Contact Person Name (2)', b.name),
    pair('Department', a.dept, 'Department', b.dept),
    pair('Designation', a.designation, 'Designation', b.designation),
    pair('Phone Number', a.phone, 'Phone Number', b.phone),
    pair('Email Address', a.email, 'Email Address', b.email),
    pair('Alt. Mobile / Extension (1)', a.altMobile, 'Alt. Mobile / Extension (2)', b.altMobile),
  ].join('');
}

/** The form as one self-contained block of HTML (inline styles only), A4 width. */
export function buildRegistrationHtml(d: RegistrationDoc): string {
  const who = d.kind === 'VENDOR' ? 'Vendor' : 'Client';
  // Two to a row; an odd last one takes the whole row.
  const others = d.otherNumbers ?? [];
  const otherRows = others.flatMap(([l1, v1], i) => {
    if (i % 2) return [];
    const next = others[i + 1];
    return [next ? pair(l1, v1, next[0], next[1]) : full(l1, v1)];
  });
  return `
<div style="font-family:Calibri,Carlito,Arial,Helvetica,sans-serif;color:#000;background:#fff;">
  <table style="width:100%;border-collapse:collapse;table-layout:fixed;border:2px solid #000;">
    <colgroup><col style="width:22%"><col style="width:28%"><col style="width:22%"><col style="width:28%"></colgroup>
    <tr><td colspan="4" style="border:${LINE};text-align:center;padding:4px 6px 3px;">
      <div style="font-weight:bold;font-size:14px;letter-spacing:0.5px;">${esc(COMPANY_NAME)}</div>
      <div style="font-size:9.5px;line-height:1.3;">${COMPANY_ADDRESS.map(esc).join(', ')} &nbsp;·&nbsp; GSTIN : ${esc(COMPANY_GSTIN)}</div>
    </td></tr>
    <tr><td colspan="4" style="border:${LINE};text-align:center;padding:4px;font-weight:bold;font-size:16px;letter-spacing:3px;">${who.toUpperCase()} FORM</td></tr>
    ${pair('Company Name', d.companyName, 'Registration ID', d.registrationId)}
    ${band(REG_SECTIONS.general)}
    ${pair(`${who} Name (Legal Name)`, d.legalName, `${who} Code`, d.code)}
    ${full('Contact Person Name', d.mainContactName)}
    ${full('Designation', d.mainContactDesignation)}
    ${d.crmName !== undefined ? pair('Business Type', d.businessType, 'CRM', d.crmName) : full('Business Type', d.businessType)}
    ${pair('Phone', d.phone, 'Email', d.email)}
    ${band(REG_SECTIONS.address)}
    ${full(d.kind === 'CLIENT' ? 'Business / Billing Address' : 'Business Address', d.address, true)}
    ${d.dispatchAddress !== undefined ? full('Dispatch Address', d.dispatchAddress, true) : ''}
    ${pair('City', d.city, 'ZIP / Postal Code', d.zip)}
    ${d.country !== undefined ? pair('State / Province', d.state, 'Country', d.country) : full('State / Province', d.state)}
    ${band(REG_SECTIONS.primary)}
    ${contactRows(d.contacts[0], d.contacts[1])}
    ${band(REG_SECTIONS.secondary)}
    ${contactRows(d.contacts[2], d.contacts[3])}
    ${band(REG_SECTIONS.tax)}
    ${pair('GST No.', d.gstNo, 'PAN No.', d.panNo)}
    ${pair('Bank Name', d.bankName, 'Bank Branch', d.bankBranch)}
    ${pair('Account Number', d.accountNo, 'IFSC Code', d.ifsc)}
    ${pair('MSME', d.msme, 'Credit Terms', d.creditTerms)}
    ${otherRows.join('')}
  </table>
</div>`;
}
